// Barcode-Scanner + Open-Food-Facts-Lookup.
//
// zxing wird lazy geladen, damit der Erst-Bundle klein bleibt.
// Die App startet ohne ZXing; erst beim ersten Klick auf "Barcode"
// wird die Lib durch esbuild's chunk-loader-Mechanismus nachgezogen.

import { api, Recipe, RecipeFull, ApiError } from "./api.js";

export interface OffProduct {
    name: string;
    kcal: number;
    protein: number;
    fat: number;
    sugar: number;
}

let zxingPromise: Promise<typeof import("@zxing/browser")> | null = null;

function loadZxing (): Promise<typeof import("@zxing/browser")> {
    if (zxingPromise === null) {
        // Bei Netzwerkfehler darf die rejected Promise nicht permanent cachen,
        // sonst kann der User auch nach Reconnect den Scanner nicht mehr nutzen.
        zxingPromise = import ("@zxing/browser").catch (err => {
            zxingPromise = null;
            throw err;
        });
    }
    return zxingPromise;
}

// Oeffnet einen Dialog mit Live-Kamera + Barcode-Decoder.
// Resolved mit dem gelesenen Code oder null bei Abbruch / Fehler.
export async function scanBarcode (): Promise<string | null> {
    let zxing: typeof import("@zxing/browser");

    try {
        zxing = await loadZxing ();
    }
    catch {
        window.alert ("Barcode-Lib konnte nicht geladen werden.");
        return null;
    }

    const dialog = document.createElement ("sl-dialog");
    dialog.setAttribute ("label", "Barcode scannen");

    const wrap = document.createElement ("div");
    wrap.style.display = "flex";
    wrap.style.flexDirection = "column";
    wrap.style.gap = "0.5rem";

    const video = document.createElement ("video");
    video.style.width = "100%";
    video.style.maxHeight = "60vh";
    video.style.background = "#000";
    video.style.borderRadius = "4px";
    video.muted = true;
    video.playsInline = true;

    const status = document.createElement ("div");
    status.className = "muted";
    status.style.fontSize = "0.875rem";
    status.textContent = "Kamera startet ...";

    const cancel = document.createElement ("sl-button");
    cancel.textContent = "Abbrechen";
    cancel.style.alignSelf = "flex-end";

    wrap.appendChild (video);
    wrap.appendChild (status);
    wrap.appendChild (cancel);
    dialog.appendChild (wrap);
    document.body.appendChild (dialog);

    (dialog as unknown as { show: () => void }).show ();

    return new Promise<string | null> ((resolve) => {
        let controls: { stop: () => void } | null = null;
        let resolved = false;

        const cleanup = () => {
            if (controls !== null) {
                try { controls.stop (); } catch { /* ignore */ }
            }

            const stream = video.srcObject as MediaStream | null;
            if (stream !== null) {
                for (const t of stream.getTracks ()) { t.stop (); }
            }

            (dialog as unknown as { hide: () => void }).hide ();
            window.setTimeout (() => dialog.remove (), 400);
        };

        const finish = (code: string | null) => {
            if (resolved) return;
            resolved = true;
            cleanup ();
            resolve (code);
        };

        cancel.addEventListener ("click", () => finish (null));
        dialog.addEventListener ("sl-after-hide", () => finish (null));

        try {
            const reader = new zxing.BrowserMultiFormatReader ();

            reader.decodeFromVideoDevice (undefined, video, (result, _err, ctl) => {
                if (controls === null && ctl !== undefined) {
                    controls = ctl;
                    status.textContent = "Code im Bild halten ...";
                }
                if (result !== null && result !== undefined) {
                    finish (result.getText ());
                }
            }).catch ((err) => {
                status.classList.add ("error");
                status.textContent = (err instanceof Error)
                    ? `Kamera nicht verfuegbar: ${err.message}`
                    : "Kamera-Fehler";
            });
        }
        catch (err) {
            status.classList.add ("error");
            status.textContent = (err instanceof Error) ? err.message : "ZXing-Fehler";
        }
    });
}

// Sucht in Open Food Facts nach einem Produktnamen. Max 10 Treffer mit gefuellten Naehrwerten.
// fetch mit Timeout (AbortController), damit ein langsames/totes Open Food
// Facts den Scan-/Such-Flow nicht unbegrenzt im Ladezustand haengen laesst.
// Ein Timeout-Abort landet im jeweiligen try/catch der Aufrufer (-> []/null).
async function fetchWithTimeout (url: string, timeoutMs = 8000): Promise<Response> {
    const ctrl = new AbortController ();
    const timer = setTimeout (() => ctrl.abort (), timeoutMs);
    try {
        return await fetch (url, { signal: ctrl.signal });
    }
    finally {
        clearTimeout (timer);
    }
}

export async function searchOpenFoodFactsByName (term: string): Promise<OffProduct[]> {
    if (term.trim ().length < 2) return [];
    const url = `https://world.openfoodfacts.org/cgi/search.pl?action=process&search_terms=${encodeURIComponent (term)}&search_simple=1&json=1&page_size=10&fields=product_name,product_name_de,code,nutriments`;
    try {
        const res = await fetchWithTimeout (url);
        if (!res.ok) return [];
        const data = await res.json () as {
            products?: Array<{
                product_name?: string;
                product_name_de?: string;
                code?: string;
                nutriments?: Record<string, unknown>;
            }>;
        };
        const out: OffProduct[] = [];
        for (const p of data.products ?? []) {
            const n = p.nutriments ?? {};
            const num = (key: string): number => {
                const v = n[key];
                return typeof v === "number" ? v : 0;
            };
            const name = (p.product_name_de ?? p.product_name ?? "").trim ();
            if (name === "") continue;
            out.push ({
                name,
                kcal: Math.round (num ("energy-kcal_100g")),
                protein: num ("proteins_100g"),
                fat: num ("fat_100g"),
                sugar: num ("sugars_100g"),
            });
        }
        return out;
    }
    catch {
        return [];
    }
}

// Schlaegt einen Barcode bei Open Food Facts nach.
// Liefert nur einen Wert, wenn das Produkt UND mindestens ein Naehrwert vorhanden sind.
export async function lookupOpenFoodFacts (barcode: string): Promise<OffProduct | null> {
    const url = `https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent (barcode)}.json`;

    try {
        const res = await fetchWithTimeout (url);

        if (!res.ok) {
            return null;
        }

        const data = await res.json () as {
            status?: number;
            product?: {
                product_name?: string;
                product_name_de?: string;
                nutriments?: Record<string, unknown>;
            };
        };

        if (data.status !== 1 || data.product === undefined) {
            return null;
        }

        const p = data.product;
        const n = p.nutriments ?? {};

        const num = (key: string): number => {
            const v = n[key];
            return typeof v === "number" ? v : 0;
        };

        return {
            name: p.product_name_de ?? p.product_name ?? `Produkt ${barcode}`,
            kcal: Math.round (num ("energy-kcal_100g")),
            protein: num ("proteins_100g"),
            fat: num ("fat_100g"),
            sugar: num ("sugars_100g"),
        };
    }
    catch {
        return null;
    }
}

// Resultat des kombinierten Scans:
// - existing != null: ein bereits angelegtes Rezept mit diesem Barcode -> Edit-Flow.
// - prefill != null: Vorschlag aus Open Food Facts (oder leerer Stub) -> Create-Flow.
export type ScanResult =
    | { kind: "existing"; barcode: string; recipe: RecipeFull }
    | { kind: "prefill";  barcode: string; prefill: Partial<Recipe> };

// Kombiniert Scan + Datenbank- + OFF-Lookup.
// Reihenfolge: 1) eigene DB nach Barcode fragen. 2) Sonst OFF.
// Rueckgabe null bei Scan-Abbruch oder Lese-Fehler.
export async function scanAndLookup (): Promise<ScanResult | null> {
    const barcode = await scanBarcode ();

    if (barcode === null || barcode === "") {
        return null;
    }

    try {
        const existing = await api.getRecipeByBarcode (barcode);
        return { kind: "existing", barcode, recipe: existing };
    }
    catch (err) {
        // 401 hat api.ts schon clearSession() aufgerufen — wir muessen das
        // hier propagieren, damit der Caller den Login-Redirect macht. Sonst
        // sieht der User OFF-Ergebnisse trotz toter Session und merkt erst
        // beim naechsten Save-Versuch dass er rausgeflogen ist.
        if (err instanceof ApiError && err.status === 401) {
            throw err;
        }
        if (!(err instanceof ApiError) || err.status !== 404) {
            // Netzwerkfehler etc. - direkt zum OFF-Fallback.
        }
    }

    const off = await lookupOpenFoodFacts (barcode);

    const prefill: Partial<Recipe> = {
        category: "food",
        reference_amount: 100,
        reference_unit: "gram",
        name: off?.name ?? `Barcode ${barcode}`,
        kcal: off?.kcal ?? 0,
        protein: off?.protein ?? 0,
        fat: off?.fat ?? 0,
        sugar: off?.sugar ?? 0,
        barcode,
    };

    return { kind: "prefill", barcode, prefill };
}
